import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  ChartConfiguration,
  ChartData,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { BarChartConfig, BarChartData } from './bar-chart.interface';

// Register Chart.js components
Chart.register(BarController, BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

// Color palette based on app's design system
const CHART_COLORS = {
  primary: '#ed3c6a', // $raspberry-darker
  secondary: '#9571c3', // $grape
  warning: '#ffc74c', // $lemon-darker
  error: '#d43b3b', // $rose-darker
  success: '#47c693', // $grass-darker
  // Additional colors for larger datasets
  night: '#5c576a', // $night
  grapeDarker: '#230b41', // $grape-darker
  raspberry: '#f69db4', // $raspberry
};

const DEFAULT_COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.warning,
  CHART_COLORS.error,
  CHART_COLORS.success,
  CHART_COLORS.night,
  CHART_COLORS.grapeDarker,
  CHART_COLORS.raspberry,
];

@Component({
  selector: 'ui-bar-chart',
  standalone: true,
  imports: [CommonModule, BaseChartDirective],
  templateUrl: './bar-chart.component.html',
})
export class BarChartComponent implements OnInit {
  @Input() data!: BarChartData;
  @Input() config: BarChartConfig = {};

  chartData!: ChartData<'bar'>;
  chartOptions!: ChartConfiguration<'bar'>['options'];

  ngOnInit(): void {
    this.setupChartData();
    this.setupChartOptions();
  }

  private setupChartData(): void {
    if (!this.data) return;

    this.chartData = {
      labels: this.data.labels,
      datasets: this.data.datasets.map((dataset, index) => ({
        label: dataset.label,
        data: dataset.data,
        backgroundColor: dataset.backgroundColor || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
        borderColor: dataset.backgroundColor || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
        borderWidth: 0,
        borderRadius: 4,
        borderSkipped: false,
        categoryPercentage: 0.9, // Reduce space between categories
      })),
    };
  }

  private setupChartOptions(): void {
    const isHorizontal = this.config.orientation === 'horizontal';

    this.chartOptions = {
      indexAxis: isHorizontal ? 'y' : 'x',
      responsive: this.config.responsive !== false,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: this.config.showLegend !== false,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              family: 'Inter, sans-serif',
              size: 12,
            },
            color: '#5c576a', // $night
          },
        },
        tooltip: {
          backgroundColor: 'rgba(23, 20, 28, 0.9)', // $night-darker with opacity
          titleFont: {
            family: 'Inter, sans-serif',
            size: 13,
          },
          bodyFont: {
            family: 'Inter, sans-serif',
            size: 12,
          },
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: 'rgba(229, 226, 233, 0.2)', // $smoke with opacity
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          padding: 12,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: {
              family: 'Inter, sans-serif',
              size: 11,
            },
            color: '#5c576a', // $night
            maxRotation: 45, // Prevent label overlap on mobile
            minRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(229, 226, 233, 0.5)', // $smoke with opacity
            lineWidth: 1,
          },
          ticks: {
            font: {
              family: 'Inter, sans-serif',
              size: 11,
            },
            color: '#5c576a', // $night
            padding: 8,
          },
        },
      },
      animation: {
        duration: 750,
        easing: 'easeInOutQuart',
      },
      interaction: {
        intersect: false,
        mode: 'index',
      },
    };
  }

  hasEmptyData(): boolean {
    if (!this.data || !this.data.datasets || this.data.datasets.length === 0) {
      return true;
    }
    return this.data.datasets.some((dataset) => !dataset.data || dataset.data.length === 0);
  }
}
